import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { OptionsAssetIcon } from './OptionsAssetIcon';
import { buildAnalyticsMetricDisplay, sanitizeTestId } from './optionsAnalyticsUtils';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';

export const OptionsAnalyticsSectionTable = ({
  title,
  rows = [],
  selectedCurrency,
  showFees = true,
  helper = null,
  showAssetIcon = false,
  testIdPrefix,
  operationsLabel = null,
}) => {
  const { t } = useOptionsLocale();
  const visibleRows = rows.filter((row) => Number(row?.metrics?.operations_count || 0) > 0);

  return (
    <section className="space-y-3" data-testid={`${testIdPrefix}-section`}>
      <div className="space-y-1" data-testid={`${testIdPrefix}-header`}>
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/45" data-testid={`${testIdPrefix}-title`}>{title}</p>
        {helper ? <p className="text-sm text-white/55" data-testid={`${testIdPrefix}-helper`}>{helper}</p> : null}
      </div>

      {!visibleRows.length ? (
        <div className="rounded-[22px] border border-dashed border-white/10 bg-white/[0.03] px-4 py-5 text-sm text-white/55" data-testid={`${testIdPrefix}-empty-state`}>
          {t('analytics.notEnoughDataForAnalysis')}
        </div>
      ) : (
        <div className="space-y-2" data-testid={`${testIdPrefix}-rows`}>
          {visibleRows.map((row) => {
          const safeKey = sanitizeTestId(row.key || row.label);
          const pnl = buildAnalyticsMetricDisplay({ metrics: row.metrics, selectedCurrency, metricKey: 'pnl', rowUnderlying: row.underlying });
          const avgPnl = buildAnalyticsMetricDisplay({ metrics: row.metrics, selectedCurrency, metricKey: 'avgPnl', rowUnderlying: row.underlying });
          const fees = buildAnalyticsMetricDisplay({ metrics: row.metrics, selectedCurrency, metricKey: 'fees', rowUnderlying: row.underlying });
          const winRate = buildAnalyticsMetricDisplay({ metrics: row.metrics, selectedCurrency, metricKey: 'winRate', rowUnderlying: row.underlying });
          const operations = buildAnalyticsMetricDisplay({ metrics: row.metrics, selectedCurrency, metricKey: 'operations', rowUnderlying: row.underlying });

          return (
            <Card key={safeKey} className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid={`${testIdPrefix}-row-${safeKey}`}>
              <CardContent className={`grid gap-3 px-4 py-4 ${showFees ? 'xl:grid-cols-[220px,repeat(4,minmax(0,1fr))]' : 'xl:grid-cols-[220px,repeat(3,minmax(0,1fr))]'}`} data-testid={`${testIdPrefix}-row-${safeKey}-content`}>
                <div className="min-w-0" data-testid={`${testIdPrefix}-row-${safeKey}-label-block`}>
                  <div className="flex items-center gap-2">
                    {showAssetIcon && row.underlying ? <OptionsAssetIcon assetKey={row.underlying} size={18} /> : null}
                    <p className="truncate text-base font-semibold text-white" data-testid={`${testIdPrefix}-row-${safeKey}-label`}>{row.label.replaceAll('_', ' ')}</p>
                  </div>
                </div>

                <MetricCell label={operationsLabel || t('analytics.operations')} display={operations} testId={`${testIdPrefix}-row-${safeKey}-operations`} />
                <MetricCell label={t('analytics.winRate')} display={winRate} testId={`${testIdPrefix}-row-${safeKey}-win-rate`} />
                <MetricCell label={t('analytics.tradingPnl')} display={pnl} testId={`${testIdPrefix}-row-${safeKey}-pnl`} />
                <MetricCell label={t('analytics.averageShort')} display={avgPnl} testId={`${testIdPrefix}-row-${safeKey}-avg`} />
                {showFees ? <MetricCell label={t('dashboard.fees')} display={fees} testId={`${testIdPrefix}-row-${safeKey}-fees`} /> : null}
              </CardContent>
            </Card>
          );
          })}
        </div>
      )}
    </section>
  );
};

const MetricCell = ({ label, display, testId }) => (
  <div className="rounded-2xl border border-white/10 bg-black/20 px-3 py-3" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-white/38" data-testid={`${testId}-label`}>{label}</p>
    <p className={`mt-1 text-sm font-semibold tabular-nums ${display.toneClass}`} data-testid={`${testId}-value`}>{display.primary}</p>
    {display.secondary ? <p className="mt-1 text-xs text-white/52 tabular-nums" data-testid={`${testId}-secondary`}>{display.secondary}</p> : null}
  </div>
);

export default OptionsAnalyticsSectionTable;